"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "sonner"
import { 
  Download, 
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  Building2,
  Users,
  FileText,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Plus,
  Receipt,
  X
} from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  initializePdfDownloadSupport,
  saveFileWithNotification,
} from "@/lib/pdfHandler"

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export default function Accounts() {
  const today = new Date()
  const defaultMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .split('T')[0]
  const defaultMonthEnd = new Date().toISOString().split('T')[0]

  const storedUser =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("user") || "{}")
      : {}
  const isStoreHandler = storedUser?.role === "store_handler"

  const [ledger, setLedger] = useState({ data: [], pagination: {} })
  const [cashbook, setCashbook] = useState({ data: [], pagination: {} })
  const [bankbook, setBankbook] = useState({ data: [], pagination: {} })
  const [gstLedger, setGstLedger] = useState({ data: [], pagination: {} })
  const [expenses, setExpenses] = useState({ data: [], pagination: {} })
  const [profitLoss, setProfitLoss] = useState(null)
  const [summary, setSummary] = useState(null)
  const [customers, setCustomers] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [expenseCategories, setExpenseCategories] = useState([])
  const [cheques, setCheques] = useState({ data: [], pagination: {} })

  const [tab, setTab] = useState(isStoreHandler ? "expenses" : "ledger")
  const [page, setPage] = useState(1)
  const [startDate, setStartDate] = useState(defaultMonthStart)
  const [endDate, setEndDate] = useState(defaultMonthEnd)
  const [searchTerm, setSearchTerm] = useState("")
  const [paymentModeFilter, setPaymentModeFilter] = useState("all")
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  // Form States
  const [expenseTitle, setExpenseTitle] = useState("")
  const [expenseAmount, setExpenseAmount] = useState("")
  const [expenseCategory, setExpenseCategory] = useState("")
  const [expensePaymentMode, setExpensePaymentMode] = useState("cash")
  const [expenseDescription, setExpenseDescription] = useState("")
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0])

  const [paymentCustomer, setPaymentCustomer] = useState("")
  const [paymentAmount, setPaymentAmount] = useState("")
  const [paymentMode, setPaymentMode] = useState("cash")
  const [paymentReference, setPaymentReference] = useState("")
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0])
  
  const [chequeNumber, setChequeNumber] = useState("")
  const [chequeDate, setChequeDate] = useState(new Date().toISOString().split('T')[0])
  const [bankName, setBankName] = useState("")

  const [supplierPaymentSupplier, setSupplierPaymentSupplier] = useState("")
  const [supplierPaymentAmount, setSupplierPaymentAmount] = useState("")
  const [supplierPaymentMode, setSupplierPaymentMode] = useState("cash")
  const [supplierPaymentDescription, setSupplierPaymentDescription] = useState("")
  const [supplierPaymentDate, setSupplierPaymentDate] = useState(new Date().toISOString().split('T')[0])
  
  const [supplierChequeNumber, setSupplierChequeNumber] = useState("")
  const [supplierChequeDate, setSupplierChequeDate] = useState(new Date().toISOString().split('T')[0])
  const [supplierBankName, setSupplierBankName] = useState("")

  const [isAddExpenseOpen, setIsAddExpenseOpen] = useState(false)
  const [isReceivePaymentOpen, setIsReceivePaymentOpen] = useState(false)
  const [isPaySupplierOpen, setIsPaySupplierOpen] = useState(false)

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : ""

  useEffect(() => {
    fetchAllData()
  }, [tab, page, startDate, endDate, paymentModeFilter])

  useEffect(() => {
    initializePdfDownloadSupport().catch(() => {
      // downloads still work even if notification setup is unavailable
    })
  }, [])

  const fetchAllData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` }

      if (isStoreHandler) {
        const params = new URLSearchParams()
        params.append('page', page)
        params.append('limit', 20)
        if (startDate) params.append('start_date', startDate)
        if (endDate) params.append('end_date', endDate)
        if (paymentModeFilter !== 'all') params.append('payment_mode', paymentModeFilter)

        const [categoriesRes, expensesRes] = await Promise.all([
          axios.get(`${API}/expense-categories`, { headers }),
          axios.get(`${API}/expenses?${params}`, { headers }),
        ])

        setExpenseCategories(categoriesRes.data)
        setExpenses(expensesRes.data)
        return
      }

      // Fetch customers and suppliers first
      const [customersRes, suppliersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/customers`, { headers }),
        axios.get(`${API}/suppliers`, { headers }),
        axios.get(`${API}/expense-categories`, { headers })
      ])

      setCustomers(customersRes.data)
      setSuppliers(suppliersRes.data)
      setExpenseCategories(categoriesRes.data)

      // Build query params
      const params = new URLSearchParams()
      params.append('page', page)
      params.append('limit', 20)
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      if (paymentModeFilter !== 'all') params.append('payment_mode', paymentModeFilter)

      // Fetch data based on tab
      if (tab === "ledger") {
        const res = await axios.get(`${API}/accounts/ledger?${params}`, { headers })
        setLedger(res.data)
      } else if (tab === "cashbook") {
        const res = await axios.get(`${API}/accounts/cashbook?${params}`, { headers })
        setCashbook(res.data)
      } else if (tab === "bankbook") {
        const res = await axios.get(`${API}/accounts/bankbook?${params}`, { headers })
        setBankbook(res.data)
      } else if (tab === "gst") {
        const res = await axios.get(`${API}/accounts/gst-ledger?${params}`, { headers })
        setGstLedger(res.data)
      } else if (tab === "expenses") {
        const res = await axios.get(`${API}/expenses?${params}`, { headers })
        setExpenses(res.data)
      } else if (tab === "cheques") {
        const res = await axios.get(`${API}/cheques?${params}`, { headers })
        setCheques(res.data)
      }

      // Always fetch summary and profit/loss
      const [summaryRes, profitRes] = await Promise.all([
        axios.get(`${API}/accounts/summary?${startDate ? 'start_date=' + startDate : ''}&${endDate ? 'end_date=' + endDate : ''}`, { headers }),
        axios.get(`${API}/accounts/profit-loss?${startDate ? 'start_date=' + startDate : ''}&${endDate ? 'end_date=' + endDate : ''}`, { headers })
      ])

      setSummary(summaryRes.data)
      setProfitLoss(profitRes.data)

    } catch (error) {
      toast.error("Failed to load accounts data")
      console.error(error)
    }
  }

  const saveExportFile = async ({ blob, fileName, title, body, folder = "accounts" }) => {
    return saveFileWithNotification({
      blob,
      fileName,
      mimeType: "text/csv",
      notificationTitle: title,
      notificationBody: body,
      relativePath: `Files/${folder}/${fileName}`,
    })
  }

  // Add Expense
  const addExpense = async () => {
    if (!expenseTitle || !expenseAmount) {
      toast.error("Enter expense details")
      return
    }

    try {
      await axios.post(
        `${API}/expenses`,
        {
          title: expenseTitle,
          amount: Number(expenseAmount),
          category_id: expenseCategory || null,
          payment_mode: expensePaymentMode,
          description: expenseDescription,
          expense_date: expenseDate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      toast.success("Expense added successfully")
      setExpenseTitle("")
      setExpenseAmount("")
      setExpenseCategory("")
      setExpenseDescription("")
      setExpenseDate(new Date().toISOString().split('T')[0])
      setIsAddExpenseOpen(false)
      fetchAllData()
    } catch (error) {
      toast.error("Failed to add expense")
      console.error(error)
    }
  }

  // Receive Payment
  const receivePayment = async () => {
    if (!paymentCustomer || !paymentAmount) {
      toast.error("Select customer and enter amount")
      return
    }

    try {
      const payload = {
        customer_id: paymentCustomer,
        amount: Number(paymentAmount),
        payment_mode: paymentMode,
        reference: paymentReference,
        payment_date: paymentDate
      }

      if (paymentMode === "cheque") {
        payload.cheque_number = chequeNumber
        payload.cheque_date = chequeDate
        payload.bank_name = bankName
      }

      await axios.post(`${API}/accounts/payment-in`, payload, { 
        headers: { Authorization: `Bearer ${token}` } 
      })

      toast.success("Payment received successfully")
      setPaymentCustomer("")
      setPaymentAmount("")
      setPaymentReference("")
      setChequeNumber("")
      setBankName("")
      setIsReceivePaymentOpen(false)
      fetchAllData()
    } catch (error) {
      toast.error("Failed to receive payment")
      console.error(error)
    }
  }

  // Pay Supplier
  const paySupplier = async () => {
    if (!supplierPaymentSupplier || !supplierPaymentAmount) {
      toast.error("Select supplier and enter amount")
      return
    }

    try {
      const payload = {
        supplier_id: supplierPaymentSupplier,
        supplier_name: suppliers.find(s => s.id === supplierPaymentSupplier)?.name || "Unknown",
        amount: Number(supplierPaymentAmount),
        payment_mode: supplierPaymentMode,
        description: supplierPaymentDescription,
        payment_date: supplierPaymentDate
      }

      if (supplierPaymentMode === "cheque") {
        payload.cheque_number = supplierChequeNumber
        payload.cheque_date = supplierChequeDate
        payload.bank_name = supplierBankName
      }

      await axios.post(`${API}/accounts/payment-out`, payload, { 
        headers: { Authorization: `Bearer ${token}` } 
      })

      toast.success("Payment made successfully")
      setSupplierPaymentSupplier("")
      setSupplierPaymentAmount("")
      setSupplierPaymentDescription("")
      setSupplierChequeNumber("")
      setSupplierBankName("")
      setIsPaySupplierOpen(false)
      fetchAllData()
    } catch (error) {
      toast.error("Failed to pay supplier")
      console.error(error)
    }
  }

  // Export Functions
  const exportLedger = async () => {
    try {
      const params = new URLSearchParams()
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)

      const response = await axios.get(`${API}/accounts/export/ledger?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })

      const downloadResult = await saveExportFile({
        blob: response.data,
        fileName: `ledger_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`,
        title: "Ledger exported",
        body: "Tap Open to view the file",
      })

      toast.success(
        downloadResult.fileUri
          ? (
            downloadResult.notificationsGranted
              ? "Ledger exported. Check your notification."
              : "Ledger exported to Files. Notification permission denied."
          )
          : "Ledger exported successfully"
      )
    } catch (error) {
      toast.error("Failed to export ledger")
    }
  }

  const exportExpenses = async () => {
    try {
      const params = new URLSearchParams()
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)

      const response = await axios.get(`${API}/accounts/export/expenses?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })

      const downloadResult = await saveExportFile({
        blob: response.data,
        fileName: `expenses_${new Date().toISOString().replace(/[:.]/g, "-")}.csv`,
        title: "Expenses exported",
        body: "Tap Open to view the file",
      })

      toast.success(
        downloadResult.fileUri
          ? (
            downloadResult.notificationsGranted
              ? "Expenses exported. Check your notification."
              : "Expenses exported to Files. Notification permission denied."
          )
          : "Expenses exported successfully"
      )
    } catch (error) {
      toast.error("Failed to export expenses")
    }
  }

  // Quick Date Filters
  const setQuickDateFilter = (filter) => {
    const today = new Date()
    let start, end

    switch(filter) {
      case 'today':
        start = end = today.toISOString().split('T')[0]
        break
      case 'week':
        start = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0]
        end = new Date().toISOString().split('T')[0]
        break
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
        end = new Date().toISOString().split('T')[0]
        break
      case 'all':
        start = end = ""
        break
    }

    setStartDate(start)
    setEndDate(end)
    setPage(1)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const getCurrentData = () => {
    switch(tab) {
      case 'ledger': return ledger
      case 'cashbook': return cashbook
      case 'bankbook': return bankbook
      case 'gst': return gstLedger
      case 'expenses': return expenses
      case 'cheques': return cheques
      default: return { data: [], pagination: {} }
    }
  }

  const currentData = getCurrentData()

  return (
    <div className="p-2 sm:p-4 md:p-6 space-y-3 sm:space-y-6 bg-background min-h-screen" data-testid="accounts-page">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4">
        <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white-900">
          {isStoreHandler ? "Expenses" : "Accounts & Ledger"}
        </h1>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {!isStoreHandler && (
            <Button 
              variant="outline" 
              onClick={exportLedger}
              className="gap-2 w-full sm:w-auto text-sm"
              data-testid="export-ledger-btn"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export Ledger</span>
              <span className="sm:hidden">Export</span>
            </Button>
          )}
          {(tab === 'expenses' || isStoreHandler) && (
            <Button 
              variant="outline" 
              onClick={exportExpenses}
              className="gap-2 w-full sm:w-auto text-sm"
              data-testid="export-expenses-btn"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export Expenses</span>
              <span className="sm:hidden">Export</span>
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {!isStoreHandler && summary && profitLoss && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 sm:gap-2 md:gap-4">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-1 sm:pb-2 pt-2 sm:pt-4 px-2 sm:px-4">
              <div className="flex items-center justify-between gap-1 sm:gap-2">
                <CardTitle className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-600 truncate">Cash Balance</CardTitle>
                <Wallet className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5 text-green-600 flex-shrink-0" />
              </div>
              <p className="text-[8px] sm:text-[10px] md:text-xs text-gray-500 mt-0.5 sm:mt-1 line-clamp-1">Cash in/out</p>
            </CardHeader>
            <CardContent className="py-1 sm:py-2 px-2 sm:px-4">
              <p className="text-sm sm:text-lg md:text-2xl font-bold text-green-600 line-clamp-2 break-words" data-testid="cash-balance">
                {formatCurrency(summary.cash_balance)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-1 sm:pb-2 pt-2 sm:pt-4 px-2 sm:px-4">
              <div className="flex items-center justify-between gap-1 sm:gap-2">
                <CardTitle className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-600 truncate">Bank Balance</CardTitle>
                <Building2 className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5 text-blue-600 flex-shrink-0" />
              </div>
              <p className="text-[8px] sm:text-[10px] md:text-xs text-gray-500 mt-0.5 sm:mt-1 line-clamp-1">Transfers</p>
            </CardHeader>
            <CardContent className="py-1 sm:py-2 px-2 sm:px-4">
              <p className="text-sm sm:text-lg md:text-2xl font-bold text-blue-600 line-clamp-2 break-words" data-testid="bank-balance">
                {formatCurrency(summary.bank_balance)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-1 sm:pb-2 pt-2 sm:pt-4 px-2 sm:px-4">
              <div className="flex items-center justify-between gap-1 sm:gap-2">
                <CardTitle className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-600 truncate">Income</CardTitle>
                <TrendingUp className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5 text-purple-600 flex-shrink-0" />
              </div>
              <p className="text-[8px] sm:text-[10px] md:text-xs text-gray-500 mt-0.5 sm:mt-1 line-clamp-1">All sales</p>
            </CardHeader>
            <CardContent className="py-1 sm:py-2 px-2 sm:px-4">
              <p className="text-sm sm:text-lg md:text-2xl font-bold text-purple-600 line-clamp-2 break-words" data-testid="total-income">
                {formatCurrency(profitLoss.income)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-1 sm:pb-2 pt-2 sm:pt-4 px-2 sm:px-4">
              <div className="flex items-center justify-between gap-1 sm:gap-2">
                <CardTitle className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-600 truncate">Expenses</CardTitle>
                <TrendingDown className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5 text-orange-600 flex-shrink-0" />
              </div>
              <p className="text-[8px] sm:text-[10px] md:text-xs text-gray-500 mt-0.5 sm:mt-1 line-clamp-1">Recorded</p>
            </CardHeader>
            <CardContent className="py-1 sm:py-2 px-2 sm:px-4">
              <p className="text-sm sm:text-lg md:text-2xl font-bold text-orange-600 line-clamp-2 break-words" data-testid="total-expenses">
                {formatCurrency(profitLoss.expense)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Profit Card */}
      {!isStoreHandler && profitLoss && (
      <Card
  className={`border-2 ${
    profitLoss.profit >= 0
      ? "border-green-500 bg-green-500/10"
      : "border-red-500 bg-red-500/10"
  }`}
>
  <CardContent className="p-2 sm:p-4 md:p-6">
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
      
      <div className="flex-1 min-w-0">
        <p className="text-[10px] sm:text-xs md:text-sm text-gray-400">Net Profit / Loss</p>

        <p
          className={`text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold mt-1 sm:mt-2 break-words ${
            profitLoss.profit >= 0 ? "text-green-400" : "text-red-400"
          }`}
        >
          {formatCurrency(profitLoss.profit)}
        </p>
      </div>

      <div
        className={`p-2 sm:p-3 md:p-4 rounded-full flex-shrink-0 ${
          profitLoss.profit >= 0
            ? "bg-green-500/20"
            : "bg-red-500/20"
        }`}
      >
        {profitLoss.profit >= 0 ? (
          <TrendingUp className="w-4 sm:w-5 md:w-6 lg:w-8 h-4 sm:h-5 md:h-6 lg:h-8 text-green-400" />
        ) : (
          <TrendingDown className="w-4 sm:w-5 md:w-6 lg:w-8 h-4 sm:h-5 md:h-6 lg:h-8 text-red-400" />
        )}
      </div>

    </div>
  </CardContent>
</Card>
      )}

      {/* Quick Actions */}
      <div className={`grid gap-2 sm:gap-3 md:gap-4 ${isStoreHandler ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"}`}>
        <Dialog open={isAddExpenseOpen} onOpenChange={setIsAddExpenseOpen}>
          <DialogTrigger asChild>
            <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-300 hover:border-orange-500">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="p-2 md:p-3 bg-orange-100 rounded-full flex-shrink-0">
                    <Receipt className="w-5 md:w-6 h-5 md:h-6 text-orange-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-base md:text-lg">Add Expense</h3>
                    <p className="text-xs md:text-sm text-gray-600 truncate">Record new</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Expense</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  placeholder="Expense title"
                  value={expenseTitle}
                  onChange={(e) => setExpenseTitle(e.target.value)}
                  data-testid="expense-title-input"
                />
              </div>
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                  data-testid="expense-amount-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={expensePaymentMode} onValueChange={setExpensePaymentMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={expenseDate}
                  onChange={(e) => setExpenseDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  placeholder="Optional description"
                  value={expenseDescription}
                  onChange={(e) => setExpenseDescription(e.target.value)}
                />
              </div>
              <Button onClick={addExpense} className="w-full" data-testid="add-expense-submit-btn">
                Add Expense
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {!isStoreHandler && <Dialog open={isReceivePaymentOpen} onOpenChange={setIsReceivePaymentOpen}>
          <DialogTrigger asChild>
            <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-300 hover:border-green-500">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Receive Payment</h3>
                    <p className="text-sm text-gray-600">From customer</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg md:text-xl">Receive Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 md:space-y-4">
              <div>
                <Label>Customer *</Label>
                <Select value={paymentCustomer} onValueChange={setPaymentCustomer}>
                  <SelectTrigger data-testid="payment-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} - {c.phone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {paymentMode === "cheque" && (
                <>
                  <div>
                    <Label>Cheque Number</Label>
                    <Input
                      placeholder="Cheque number"
                      value={chequeNumber}
                      onChange={(e) => setChequeNumber(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Cheque Date</Label>
                    <Input
                      type="date"
                      value={chequeDate}
                      onChange={(e) => setChequeDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      placeholder="Bank name"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                    />
                  </div>
                </>
              )}
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Reference</Label>
                <Input
                  placeholder="Optional reference"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </div>
              <Button onClick={receivePayment} className="w-full bg-green-600 hover:bg-green-700" data-testid="receive-payment-submit-btn">
                Receive Payment
              </Button>
            </div>
          </DialogContent>
        </Dialog>}

        {!isStoreHandler && <Dialog open={isPaySupplierOpen} onOpenChange={setIsPaySupplierOpen}>
          <DialogTrigger asChild>
            <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-300 hover:border-red-500">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="p-2 md:p-3 bg-red-100 rounded-full flex-shrink-0">
                    <Users className="w-5 md:w-6 h-5 md:h-6 text-red-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-base md:text-lg">Pay Supplier</h3>
                    <p className="text-xs md:text-sm text-gray-600 truncate">Make payment</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg md:text-xl">Pay Supplier</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 md:space-y-4">
              <div>
                <Label>Supplier *</Label>
                <Select value={supplierPaymentSupplier} onValueChange={setSupplierPaymentSupplier}>
                  <SelectTrigger data-testid="supplier-select">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} - {s.phone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={supplierPaymentAmount}
                  onChange={(e) => setSupplierPaymentAmount(e.target.value)}
                  data-testid="supplier-payment-amount-input"
                />
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={supplierPaymentMode} onValueChange={setSupplierPaymentMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {supplierPaymentMode === "cheque" && (
                <>
                  <div>
                    <Label>Cheque Number</Label>
                    <Input
                      placeholder="Cheque number"
                      value={supplierChequeNumber}
                      onChange={(e) => setSupplierChequeNumber(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Cheque Date</Label>
                    <Input
                      type="date"
                      value={supplierChequeDate}
                      onChange={(e) => setSupplierChequeDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      placeholder="Bank name"
                      value={supplierBankName}
                      onChange={(e) => setSupplierBankName(e.target.value)}
                    />
                  </div>
                </>
              )}
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={supplierPaymentDate}
                  onChange={(e) => setSupplierPaymentDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  placeholder="Optional description"
                  value={supplierPaymentDescription}
                  onChange={(e) => setSupplierPaymentDescription(e.target.value)}
                />
              </div>
              <Button onClick={paySupplier} variant="destructive" className="w-full" data-testid="pay-supplier-submit-btn">
                Pay Supplier
              </Button>
            </div>
          </DialogContent>
        </Dialog>}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 md:p-4">
          <div className="mb-3 flex items-center justify-between md:hidden">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMobileFilters((current) => !current)}
              className="h-9 gap-2 rounded-xl px-3 text-xs"
            >
              {showMobileFilters ? <X className="h-3.5 w-3.5" /> : <Filter className="h-3.5 w-3.5" />}
              {showMobileFilters ? "Close Filters" : "Open Filters"}
            </Button>
          </div>

          <div className={`${showMobileFilters ? "flex" : "hidden"} flex-col gap-3 md:flex md:gap-4 md:flex-wrap md:flex-row md:items-end`}>
            <div className="flex-1 min-w-full md:min-w-[160px]">
              <Label className="text-xs md:text-sm">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1) }}
                data-testid="start-date-filter"
                className="text-sm"
              />
            </div>
            <div className="flex-1 min-w-full md:min-w-[160px]">
              <Label className="text-xs md:text-sm">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1) }}
                data-testid="end-date-filter"
                className="text-sm"
              />
            </div>
            <div className="flex gap-1 md:gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setQuickDateFilter('today')} className="text-xs md:text-sm px-2 md:px-3 py-1 md:py-2">Today</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateFilter('week')} className="text-xs md:text-sm px-2 md:px-3 py-1 md:py-2">Week</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateFilter('month')} className="text-xs md:text-sm px-2 md:px-3 py-1 md:py-2">Month</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateFilter('all')} className="text-xs md:text-sm px-2 md:px-3 py-1 md:py-2">All</Button>
            </div>
      {!isStoreHandler && (tab === 'ledger' || tab === 'cashbook' || tab === 'bankbook') && (
              <div className="flex-1 min-w-full md:min-w-[160px]">
                <Label className="text-xs md:text-sm">Mode</Label>
                <Select value={paymentModeFilter} onValueChange={(val) => { setPaymentModeFilter(val); setPage(1) }}>
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(val) => { setTab(val); setPage(1) }} className="space-y-4">
        <TabsList className={`grid w-full ${isStoreHandler ? "grid-cols-1" : "grid-cols-3 md:grid-cols-6"} lg:w-auto h-auto`}>
          {!isStoreHandler && <TabsTrigger value="ledger" data-testid="ledger-tab" className="text-xs md:text-sm py-2 md:py-3">Ledger</TabsTrigger>}
          {!isStoreHandler && <TabsTrigger value="cashbook" data-testid="cashbook-tab" className="text-xs md:text-sm py-2 md:py-3">Cashbook</TabsTrigger>}
          {!isStoreHandler && <TabsTrigger value="bankbook" data-testid="bankbook-tab" className="text-xs md:text-sm py-2 md:py-3">Bankbook</TabsTrigger>}
          {!isStoreHandler && <TabsTrigger value="gst" data-testid="gst-tab" className="text-xs md:text-sm py-2 md:py-3">GST</TabsTrigger>}
          <TabsTrigger value="expenses" data-testid="expenses-tab" className="text-xs md:text-sm py-2 md:py-3">Expenses</TabsTrigger>
          {!isStoreHandler && <TabsTrigger value="cheques" data-testid="cheques-tab" className="text-xs md:text-sm py-2 md:py-3">Cheques</TabsTrigger>}
        </TabsList>

        {/* Ledger Tab */}
        <TabsContent value="ledger">
          <Card>
            <CardHeader className="py-3 md:py-4 px-3 md:px-6">
              <CardTitle className="text-base md:text-lg">General Ledger</CardTitle>
            </CardHeader>
            <CardContent className="px-2 md:px-6 py-3 md:py-4">
              <div className="overflow-x-auto rounded-lg border border-border">
  <table className="w-full text-xs md:text-sm">
    
    <thead className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b-2 border-blue-500/50 sticky top-0 z-10 shadow-lg">
      <tr className="text-slate-100">
        <th className="text-left p-2 md:p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Date</th>
        <th className="text-left p-2 md:p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Type</th>
        <th className="text-left p-2 md:p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300 hidden sm:table-cell">Desc</th>
        <th className="text-right p-2 md:p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-red-300">Debit</th>
        <th className="text-right p-2 md:p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-green-300">Credit</th>
        <th className="text-right p-2 md:p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-purple-300 hidden md:table-cell">Balance</th>
        <th className="text-left p-2 md:p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300 hidden lg:table-cell">Mode</th>
      </tr>
    </thead>

    <tbody>
      {ledger.data?.length > 0 ? (
        ledger.data.map((entry, i) => (
          <tr key={i} className="border-b border-border hover:bg-muted/30 transition">
            
            <td className="p-2 md:p-3 text-xs md:text-sm">{formatDate(entry.date)}</td>

            <td className="p-2 md:p-3">
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  entry.type === "sale"
                    ? "bg-green-100 text-green-700"
                    : entry.type === "expense"
                    ? "bg-red-100 text-red-700"
                    : entry.type === "payment_in"
                    ? "bg-blue-100 text-blue-700"
                    : entry.type === "payment_out"
                    ? "bg-orange-100 text-orange-700"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {entry.type.slice(0, 3)}
              </span>
            </td>

            <td className="p-2 md:p-3 hidden sm:table-cell truncate text-xs md:text-sm">{entry.description}</td>

            <td className="p-2 md:p-3 text-right text-red-500 font-semibold text-xs md:text-sm">
              {entry.debit ? formatCurrency(entry.debit).slice(0, 8) : "-"}
            </td>

            <td className="p-2 md:p-3 text-right text-green-500 font-semibold text-xs md:text-sm">
              {entry.credit ? formatCurrency(entry.credit).slice(0, 8) : "-"}
            </td>

            <td
              className={`p-2 md:p-3 text-right font-bold hidden md:table-cell text-xs md:text-sm ${
                entry.balance >= 0 ? "text-green-500" : "text-red-500"
              }`}
            >
              {formatCurrency(entry.balance).slice(0, 8)}
            </td>

            <td className="p-2 md:p-3 hidden lg:table-cell">
              <span className="px-2 py-1 bg-muted rounded text-xs">
                {entry.mode}
              </span>
            </td>

          </tr>
        ))
      ) : (
        <tr>
          <td colSpan="7" className="p-6 text-center text-muted-foreground text-sm">
            No ledger entries found
          </td>
        </tr>
      )}
    </tbody>

  </table>
</div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cashbook Tab */}
        <TabsContent value="cashbook">
          <Card>
            <CardHeader>
              <CardTitle>Cash Book</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b-2 border-blue-500/50 sticky top-0 z-10 shadow-lg">
                    <tr className="text-slate-100">
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Date</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Description</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-red-300">Debit</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-green-300">Credit</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-purple-300">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashbook.data && cashbook.data.length > 0 ? (
                      cashbook.data.map((entry, i) => (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="p-3">{formatDate(entry.date)}</td>
                          <td className="p-3">{entry.description}</td>
                          <td className="p-3 text-right text-red-600 font-medium">
                            {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                          </td>
                          <td className="p-3 text-right text-green-600 font-medium">
                            {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                          </td>
                          <td className={`p-3 text-right font-bold ${entry.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(entry.balance)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="p-6 text-center text-gray-500">
                          No cash transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bankbook Tab */}
        <TabsContent value="bankbook">
          <Card>
            <CardHeader>
              <CardTitle>Bank Book</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b-2 border-blue-500/50 sticky top-0 z-10 shadow-lg">
                    <tr className="text-slate-100">
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Date</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Description</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Mode</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-red-300">Debit</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-green-300">Credit</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-purple-300">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankbook.data && bankbook.data.length > 0 ? (
                      bankbook.data.map((entry, i) => (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="p-3">{formatDate(entry.date)}</td>
                          <td className="p-3">{entry.description}</td>
                          <td className="p-3">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {entry.mode}
                            </span>
                          </td>
                          <td className="p-3 text-right text-red-600 font-medium">
                            {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                          </td>
                          <td className="p-3 text-right text-green-600 font-medium">
                            {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                          </td>
                          <td className={`p-3 text-right font-bold ${entry.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(entry.balance)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="p-6 text-center text-gray-500">
                          No bank transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GST Tab */}
        <TabsContent value="gst">
          <Card>
            <CardHeader>
              <CardTitle>GST Ledger</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b-2 border-blue-500/50 sticky top-0 z-10 shadow-lg">
                    <tr className="text-slate-100">
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Date</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Invoice</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Customer</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-orange-300">Taxable</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-orange-300">CGST</th>
                      <th className="text-right p-3 font-semibold">SGST</th>
                      <th className="text-right p-3 font-semibold">IGST</th>
                      <th className="text-right p-3 font-semibold">Total GST</th>
                      <th className="text-right p-3 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gstLedger.data && gstLedger.data.length > 0 ? (
                      gstLedger.data.map((entry, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="p-3">{formatDate(entry.date)}</td>
                          <td className="p-3 font-medium">{entry.invoice}</td>
                          <td className="p-3">{entry.customer}</td>
                          <td className="p-3 text-right">{formatCurrency(entry.taxable_amount)}</td>
                          <td className="p-3 text-right text-blue-600">{formatCurrency(entry.cgst)}</td>
                          <td className="p-3 text-right text-purple-600">{formatCurrency(entry.sgst)}</td>
                          <td className="p-3 text-right text-orange-600">{formatCurrency(entry.igst)}</td>
                          <td className="p-3 text-right font-medium text-green-600">{formatCurrency(entry.gst_amount)}</td>
                          <td className="p-3 text-right font-bold">{formatCurrency(entry.total)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="p-6 text-center text-gray-500">
                          No GST entries found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <Card>
            <CardHeader>
              <CardTitle>Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b-2 border-blue-500/50 sticky top-0 z-10 shadow-lg">
                    <tr className="text-slate-100">
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Date</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Title</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-red-300">Amount</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Mode</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Description</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.data && expenses.data.length > 0 ? (
                      expenses.data.map((exp, i) => (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="p-3">{formatDate(exp.expense_date)}</td>
                          <td className="p-3 font-medium">{exp.title}</td>
                          <td className="p-3 text-right font-bold text-red-600">{formatCurrency(exp.amount)}</td>
                          <td className="p-3">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                              {exp.payment_mode}
                            </span>
                          </td>
                          <td className="p-3 text-gray-600">{exp.description || '-'}</td>
                          <td className="p-3">{exp.created_by_name || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="p-6 text-center text-gray-500">
                          No expenses found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cheques Tab */}
        <TabsContent value="cheques">
          <Card>
            <CardHeader>
              <CardTitle>Cheque Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b-2 border-blue-500/50 sticky top-0 z-10 shadow-lg">
                    <tr className="text-slate-100">
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Cheque No.</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Date</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Party</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Type</th>
                      <th className="text-right p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-purple-300">Amount</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Bank</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-green-300">Status</th>
                      <th className="text-left p-3 font-bold text-xs md:text-sm uppercase tracking-wider text-blue-300">Cleared Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cheques.data && cheques.data.length > 0 ? (
                      cheques.data.map((cheque, i) => (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="p-3 font-medium">{cheque.cheque_number}</td>
                          <td className="p-3">{formatDate(cheque.cheque_date)}</td>
                          <td className="p-3">{cheque.party_name}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              cheque.party_type === 'customer' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                            }`}>
                              {cheque.party_type}
                            </span>
                          </td>
                          <td className="p-3 text-right font-bold">{formatCurrency(cheque.amount)}</td>
                          <td className="p-3">{cheque.bank_name || '-'}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              cheque.status === 'cleared' ? 'bg-green-100 text-green-800' :
                              cheque.status === 'bounced' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {cheque.status}
                            </span>
                          </td>
                          <td className="p-3">{formatDate(cheque.cleared_date)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="p-6 text-center text-gray-500">
                          No cheques found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pagination */}
      {currentData.pagination && currentData.pagination.total_pages > 1 && (
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <p className="text-xs md:text-sm text-gray-600 break-words">
                Page <span className="font-semibold">{currentData.pagination.page}</span> of <span className="font-semibold">{currentData.pagination.total_pages}</span> ({currentData.pagination.total} entries)
              </p>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  data-testid="prev-page-btn"
                  className="flex-1 sm:flex-none text-xs md:text-sm"
                >
                  <ChevronLeft className="w-3 md:w-4 h-3 md:h-4" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= currentData.pagination.total_pages}
                  onClick={() => setPage(page + 1)}
                  data-testid="next-page-btn"
                  className="flex-1 sm:flex-none text-xs md:text-sm"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="w-3 md:w-4 h-3 md:h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  )
}

